import { normalizeText } from '@umodeapporg/ui/helpers/stringHelpers';

import { buildDateRange, buildSingleValue, readDateRange, TFilterBuilderValues } from '@/components/filters/filterBuilder';
import { clientesUnicos, incluiCliente } from '@/domain/common/helpers/clienteFiltro';
import { GRAVIDADES, TGravidadeFarol } from '@/domain/ops/helpers/gravidadeDoFarol';
import { EnrichedOp, FarolCor, FonteTempo, SituacaoOp } from '@/domain/ops/interfaces/ops';

import { isFarolCor, isSituacaoOp } from './formatters';

/** Every source the engine can land on, in the order the filter offers them. */
export const FONTES_TEMPO: Array<FonteTempo> = [
  'VESTO',
  'SEQUENCIA_ESTIMADA',
  'SEQUENCIA_INFORMADA',
  'TEMPO_INFORMADO_PRODUTO',
  'SEQUENCIA_MEDIDA',
  'PRODUTO_SIMILAR',
  'CURADO_OP',
  'NENHUMA',
];

/**
 * The risk cut one chip applies: everything that will miss the deadline, or only one of
 * its two halves. One value at a time, because the three answers already cover each other.
 */
export type RiscoFiltro = 'EM_RISCO' | 'JA_VENCIDO' | 'VAI_ATRASAR';

export const RISCO_FILTROS: Array<RiscoFiltro> = ['EM_RISCO', 'JA_VENCIDO', 'VAI_ATRASAR'];

function isRiscoFiltro(value: string): value is RiscoFiltro {
  return RISCO_FILTROS.some((filtro) => filtro === value);
}

/**
 * Os indicadores da faixa de cartões, como recorte da tabela: são os MESMOS três números
 * que os cartões contam, e por isso clicar num cartão e escolher o filtro no "+ Filtro"
 * chegam na mesma lista. Quem decide o que entra em cada um é `opsIndicadores.ts`, uma
 * regra só lida pelos cartões e pelo filtro.
 */
export type TIndicadorOp = 'ENTREGA_VENCIDA' | 'MATERIAL_PENDENTE' | 'SEM_TEMPO';

export const INDICADORES_OP: Array<TIndicadorOp> = ['ENTREGA_VENCIDA', 'MATERIAL_PENDENTE', 'SEM_TEMPO'];

export function isIndicadorOp(value: string): value is TIndicadorOp {
  return INDICADORES_OP.some((indicador) => indicador === value);
}

/**
 * Each filter holds a LIST, because the builder lets one chip carry several values: they are
 * alternatives, so an OP that matches any of them stays. Different chips still narrow each
 * other.
 */
export interface IOpsFilters {
  farol: Array<FarolCor>;
  marca: Array<string>;
  /** Client (legal name): one client brings every brand of its group. */
  cliente: Array<string>;
  celula: Array<string>;
  /** The agreed sewing order: `SIM` or `NAO`; empty means it is not narrowing anything. */
  validada: Array<string>;
  fonte: Array<FonteTempo>;
  /** Free text matched against the product name AND its group. */
  produto: Array<string>;
  /** Delivery window, as `YYYY-MM-DD`. */
  entregaDe: string;
  entregaAte: string;
  /** The OPs that miss the deadline; empty means the risk is not narrowing anything. */
  risco: RiscoFiltro | '';
  /** The indicators of the cards: empty means no card is narrowing the table. */
  indicador: Array<TIndicadorOp>;
  /** O grau do vermelho; vazio quando a gravidade não está recortando nada. */
  gravidade: Array<TGravidadeFarol>;
}

/** The query keys of the filters that narrow the list. */
export const OPS_FILTER_KEYS = [
  'farol',
  'marca',
  'cliente',
  'celula',
  'validada',
  'fonte',
  'produto',
  'entregaDe',
  'entregaAte',
  'risco',
  'indicador',
  'gravidade',
] as const;

/** The situation key in the link: it does not narrow the list, it picks the set. */
export const SITUACAO_KEY = 'situacao';

/**
 * What "clear filters" drops from the link: the filters PLUS the situation, because
 * whoever clears expects the portfolio back. Staying on the exits without noticing is
 * how somebody reads the wrong number.
 */
export const OPS_CLEAR_KEYS = [...OPS_FILTER_KEYS, SITUACAO_KEY] as const;

export const EMPTY_OPS_FILTERS: IOpsFilters = {
  farol: [],
  marca: [],
  cliente: [],
  celula: [],
  validada: [],
  fonte: [],
  produto: [],
  entregaDe: '',
  entregaAte: '',
  risco: '',
  indicador: [],
  gravidade: [],
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** One filter, several values, one link: `farol=VERMELHO,AMARELO`. */
const LIST_SEPARATOR = ',';

/** A repeated value would filter twice for the same thing, so the list is read as a set. */
function readList(raw: string): Array<string> {
  const values = raw
    .split(LIST_SEPARATOR)
    .map((value) => value.trim())
    .filter((value) => value !== '');
  return Array.from(new Set(values));
}

/** A hand-typed link may say `farol=vermelho`, so the values are read case-insensitively. */
function readFarois(raw: string): Array<FarolCor> {
  return readList(raw)
    .map((value) => value.toUpperCase())
    .filter(isFarolCor);
}

function isFonteTempo(value: string): value is FonteTempo {
  return FONTES_TEMPO.some((fonte) => fonte === value);
}

function readFontes(raw: string): Array<FonteTempo> {
  return readList(raw)
    .map((value) => value.toUpperCase())
    .filter(isFonteTempo);
}

function readIsoDate(raw: string): string {
  const value = raw.trim();
  return ISO_DATE.test(value) ? value : '';
}

function readRisco(raw: string): RiscoFiltro | '' {
  const value = raw.trim().toUpperCase();
  return isRiscoFiltro(value) ? value : '';
}

function readIndicadores(raw: string): Array<TIndicadorOp> {
  return readList(raw)
    .map((value) => value.toUpperCase())
    .filter(isIndicadorOp);
}

/** Os graus vão em minúsculas no link, como o tipo os escreve: `gravidade=grave,medio`. */
function readGravidades(raw: string): Array<TGravidadeFarol> {
  return readList(raw)
    .map((value) => value.toLowerCase())
    .filter((value): value is TGravidadeFarol => GRAVIDADES.some((grau) => grau === value));
}

/** The link IS the filter state: anything unreadable is simply treated as "no filter". */
export function parseOpsFilters(getParam: (key: string) => string): IOpsFilters {
  return {
    farol: readFarois(getParam('farol')),
    marca: readList(getParam('marca')),
    cliente: readList(getParam('cliente')),
    celula: readList(getParam('celula')),
    validada: readList(getParam('validada')),
    fonte: readFontes(getParam('fonte')),
    produto: readList(getParam('produto')),
    entregaDe: readIsoDate(getParam('entregaDe')),
    entregaAte: readIsoDate(getParam('entregaAte')),
    risco: readRisco(getParam('risco')),
    indicador: readIndicadores(getParam('indicador')),
    gravidade: readGravidades(getParam('gravidade')),
  };
}

/** The delivery window is ONE chip with two dates, so the builder knows it by one key. */
export const OPS_ENTREGA_KEY = 'entrega';

/** What the filter bar shows: one entry per chip, the window as its two ends. */
export function toOpsBuilderValues(filters: IOpsFilters): TFilterBuilderValues {
  return {
    farol: filters.farol,
    marca: filters.marca,
    cliente: filters.cliente,
    celula: filters.celula,
    validada: filters.validada,
    fonte: filters.fonte,
    produto: filters.produto,
    [OPS_ENTREGA_KEY]: buildDateRange({ from: filters.entregaDe, to: filters.entregaAte }),
    risco: buildSingleValue(filters.risco),
    indicador: filters.indicador,
    gravidade: filters.gravidade,
  };
}

/** What one confirmed chip writes in the address bar, which is where the filter lives. */
export function opsFilterPatch(key: string, value: Array<string>): Record<string, string | null> {
  if (key === OPS_ENTREGA_KEY) {
    const range = readDateRange(value);
    return { entregaDe: range.from, entregaAte: range.to };
  }
  return { [key]: value.join(LIST_SEPARATOR) };
}

/** Which keys one chip owns, so removing it leaves nothing of its own behind in the link. */
export function opsFilterKeysOf(key: string): Array<string> {
  return key === OPS_ENTREGA_KEY ? ['entregaDe', 'entregaAte'] : [key];
}

/** The same removal as a patch, so dropping one chip and writing another is ONE navigation. */
export function opsClearPatch(key: string | null): Record<string, null> {
  if (key === null) {
    return {};
  }
  return Object.fromEntries(opsFilterKeysOf(key).map((param) => [param, null]));
}

/**
 * Which set the screen lists. It is not one of the filters above: it decides what the
 * SERVER returns, and an unreadable link simply opens the portfolio, as always.
 */
export function parseSituacaoOp(raw: string): SituacaoOp {
  const value = raw.trim().toLowerCase();
  return isSituacaoOp(value) ? value : 'carteira';
}

export function hasActiveOpsFilters(filters: IOpsFilters): boolean {
  return (
    filters.farol.length > 0 ||
    filters.marca.length > 0 ||
    filters.cliente.length > 0 ||
    filters.celula.length > 0 ||
    filters.validada.length > 0 ||
    filters.fonte.length > 0 ||
    filters.produto.length > 0 ||
    filters.entregaDe !== '' ||
    filters.entregaAte !== '' ||
    filters.risco !== '' ||
    filters.indicador.length > 0 ||
    filters.gravidade.length > 0
  );
}

/** The delivery the row shows: the renegotiated date wins over the original one. */
export function entregaDaOp(op: EnrichedOp): string | null {
  return op.dataEntregaRepactuada ?? op.dataEntrega;
}

/**
 * O valor vem do seletor (nomes inteiros do catálogo), então a comparação é pelo nome
 * INTEIRO, não por "contém": com "contém", escolher "CAMISETA FEM INK ONDEMAND MP"
 * trazia junto as OPs de "CAMISETA FEM INK ONDEMAND MP PLUS", e aí a Carteira mostrava
 * mais OPs do que a ficha daquele produto soma.
 */
function matchesProdutoOuGrupo(op: EnrichedOp, termo: string): boolean {
  const term = normalizeText(termo);
  return normalizeText(op.produto) === term || normalizeText(op.grupo) === term;
}

/**
 * Every filter narrows the one before it, and the values INSIDE one filter are alternatives.
 * An OP with no delivery date drops out as soon as a delivery window is set: it cannot be
 * shown to be inside a window it has no date for.
 *
 * `filters.risco` is NOT applied here: it needs today's date, which no filter carries, so
 * the screen composes it with `matchesRiscoFiltro` (`opsRisco.ts`).
 */
export function matchesOpsFilters(op: EnrichedOp, filters: IOpsFilters): boolean {
  if (filters.farol.length > 0 && !filters.farol.includes(op.farol.cor)) {
    return false;
  }
  if (filters.marca.length > 0 && !filters.marca.includes(op.marca)) {
    return false;
  }
  // By CLIENT, so the chip brings every brand of the group: it is the client the OP
  // carries that decides, never its brand.
  if (filters.cliente.length > 0 && !incluiCliente(filters.cliente, op.cliente)) {
    return false;
  }
  if (filters.celula.length > 0 && !filters.celula.includes(op.celulaEfetiva ?? '')) {
    return false;
  }
  // A ordem combinada com o chão de fábrica: "SIM" e "NAO" são os dois estados do campo.
  if (filters.validada.length > 0 && !filters.validada.includes(op.validada === true ? 'SIM' : 'NAO')) {
    return false;
  }
  if (filters.fonte.length > 0 && !filters.fonte.includes(op.tempo.fonte)) {
    return false;
  }
  if (filters.produto.length > 0 && !filters.produto.some((termo) => matchesProdutoOuGrupo(op, termo))) {
    return false;
  }
  const entrega = entregaDaOp(op);
  if (filters.entregaDe !== '' && (entrega === null || entrega < filters.entregaDe)) {
    return false;
  }
  if (filters.entregaAte !== '' && (entrega === null || entrega > filters.entregaAte)) {
    return false;
  }
  return true;
}

/** The text the table's own search box runs over, and the export runs over the same one. */
export function opSearchableText(op: EnrichedOp): string {
  return [op.opRaw, op.produto, op.cor, op.marca, op.grupo, op.celulaEfetiva ?? ''].join(' ');
}

export function matchesOpSearch(op: EnrichedOp, search: string): boolean {
  const term = normalizeText(search).trim();
  if (term === '') {
    return true;
  }
  return normalizeText(opSearchableText(op)).includes(term);
}

export interface IOpsFilterOptions {
  marcas: Array<string>;
  /** Legal names present in the portfolio, one entry per client. */
  clientes: Array<string>;
  celulas: Array<string>;
  /** Product names and group names together: the box filters by either. */
  produtos: Array<string>;
}

function sortedUnique(values: Array<string>): Array<string> {
  return Array.from(new Set(values.filter((value) => value.trim() !== ''))).sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/** Options come from the portfolio itself, so a brand with no OP is never offered. */
export function buildOpsFilterOptions(ops: Array<EnrichedOp>): IOpsFilterOptions {
  return {
    marcas: sortedUnique(ops.map((op) => op.marca)),
    clientes: clientesUnicos(ops.map((op) => op.cliente)),
    celulas: sortedUnique(ops.map((op) => op.celulaEfetiva ?? '')),
    produtos: sortedUnique([...ops.map((op) => op.produto), ...ops.map((op) => op.grupo)]),
  };
}
